import { Search } from '@/components/molecules/Search';
import { DefaultToolbar } from '@/components/molecules/Toolbar';
import { Filters } from '../Filters';
import { IntakeDropdown } from '@/components/molecules/IntakeDropdown';
import { InstitutionDropdown } from '@/components/molecules/InstituteDropdown';
import { getStudentDashboardFilterGroups } from '@/utilities/getFilterGroups/getStudentsDashboardFilters';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { ISystemAndUniConfig } from 'api-paths-cas';
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { getAgencies } from '@/store/agency/actions';
import { programmableTagsActions } from '@/store/programmable_tags/reducer';
import { getAllRegionConfigRecords } from '@/store/region_config_dashboard/actions';
import { getActiveFiltersFromParams } from '../Filters/helpers';
import { ActionToolbar } from '@/components/molecules/ActionsToolbar';
import { useGetActiveTab } from '@/utilities/useGetActiveTab';
import { QueryKeys } from '@/types';
import { DashboardPagination } from '@/components/molecules/Pagination';
import { MessageFilter } from '@/components/molecules/MessageFilter';
import { ExportAllButton } from '@/components/atoms/ExportAllButton/ExportAllButton';
import { IncludeStudentsDropdown } from '@/components/molecules/IncludeStudentsDropdown';
import { UserRole } from '@/store/auth/types';
import { StyledDivider } from '@/components/atoms/StyledDivider';
import { requestStudents, requestTotals } from '../../../store/dashboard/actions';
import { getIntakeId } from '@/store/selectors';
import { DataGridHeader } from '../DataGridHeader';

function useGetFilterOptions(uniConfig, userRole, pathway, intakeId): void {
  const dispatch = useDispatch();
  useEffect(() => {
    // Only request these options if we have an intakeId
    if (!intakeId || !uniConfig.isLoaded) {
      return;
    }

    // Remove requestFilters() - let CASDataGrid handle it based on needsFreshTotals logic
    // dispatch(requestFilters());
    if (hasPerm(userRole, P.FILTER_AGENTS)) {
      dispatch(getAgencies());
    }
    dispatch(programmableTagsActions.getProgrammableTagRecords());
    dispatch(getAllRegionConfigRecords(uniConfig.university_code, pathway));
  }, [dispatch, intakeId, uniConfig.isLoaded]);
}

const defaultTab = 'Pre-Deposit';

/**
 * Organism that combines the filters saga, the generic default toolbar molecule, search, filters,
 * intake, and institution generic molecules, into a specialised cas dashboard toolbar.
 */
export function CASToolbar(): ReactNode {
  const dispatch = useDispatch();
  const uniConfig = useSelector((state: ApplicationState) => state.config) as ISystemAndUniConfig;
  const intakeId = useSelector(getIntakeId);

  const { isPathway, pathway, userRole, isPartOfPathway, pathwayAccess } = useSelector(
    ({ auth, config }: ApplicationState) => ({
      isPathway: auth?.isPathway,
      pathway: auth?.pathway,
      pathwayAccess: auth?.pathwayAccess,
      isPartOfPathway: config?.relatedPathways?.length,
      userRole: auth?.role,
    }),
  );

  useGetFilterOptions(uniConfig, userRole, pathway, intakeId);

  const { students, filterOptionsLoading, filterOptions, selectedRows, tabs } = useSelector(
    ({ dashboard }: ApplicationState) => ({
      students: dashboard.students,
      filterOptionsLoading: dashboard.filterOptionsLoading,
      filters: dashboard.filters,
      filterOptions: dashboard.filterOptions,
      selectedRows: dashboard.selectedRows,
      tabs: dashboard.tabs,
    }),
  );

  const programmableTags = useSelector((state: ApplicationState) => state.programmableTags.programmableTags);
  const agents = useSelector((state: ApplicationState) => state.agencies.list);
  const regionConfigs = useSelector((state: ApplicationState) => state.regionConfigs);

  const refreshRowsCallback = useCallback(() => {
    if (!intakeId) {
      console.log('[CASToolbar] No intakeId available, skipping student request');
      return;
    }
    dispatch(requestStudents());
    dispatch(requestTotals());
  }, [dispatch, intakeId]);

  const activeTab = useGetActiveTab(defaultTab, QueryKeys.STAGE);

  const filterGroups = useMemo(() => {
    if (!uniConfig || !regionConfigs || filterOptionsLoading) return [];
    const activeFilters = getActiveFiltersFromParams(new URLSearchParams(window.location.search));
    const studentDashboardFilters = getStudentDashboardFilterGroups({
      activeFilters,
      agents,
      regionConfigs,
      config: uniConfig,
      isPathway,
      programmableTags,
      userRole,
      students,
      filterOptions,
    });
    return studentDashboardFilters;
  }, [regionConfigs, uniConfig, userRole, isPathway, students, filterOptions, filterOptionsLoading, activeTab]);

  const canViewUniSelector = hasPerm(userRole, P.MULTI_UNIVERSITY_VIEW);
  const canViewIncludeStudentDropdown = !!(
    isPartOfPathway &&
    hasPerm(userRole, P.VIEW_INSTITUTION_DROPDOWN) &&
    !(
      [UserRole.UniAdmin, UserRole.Uni, UserRole.UniSecure, UserRole.UniLite].includes(userRole) &&
      pathwayAccess === 'None'
    )
  );

  const headerComponents = [
    <React.Fragment key="search-filters">
      <Search callbackOnSelect={refreshRowsCallback} />

      <StyledDivider />

      <Filters
        refreshRowsCallback={refreshRowsCallback}
        filterGroups={filterGroups}
        filterOptionsLoading={filterOptionsLoading}
      />

      <StyledDivider />

      <MessageFilter callbackOnSelect={refreshRowsCallback} />

      <ExportAllButton />
    </React.Fragment>,
    <DashboardPagination
      key="pagination"
      onNewPage={refreshRowsCallback}
      showRangeSelector={true}
      defaultTab={defaultTab}
      queryKey={QueryKeys.STAGE}
      tabs={tabs}
    />,
  ];

  const activeComponents = [
    <React.Fragment key="active-dropdowns">
      {canViewUniSelector && <InstitutionDropdown />}
      {canViewIncludeStudentDropdown && <IncludeStudentsDropdown />}
      <IntakeDropdown />
    </React.Fragment>,
  ];

  const toolbarComponent =
    selectedRows.length > 0 ? (
      <ActionToolbar selectedRows={selectedRows} />
    ) : (
      <DefaultToolbar HeaderComponents={headerComponents} />
    );

  return <DataGridHeader title={activeTab} activeComponent={activeComponents} toolbarComponent={toolbarComponent} />;
}
